import React, { useEffect, useState } from "react";

import { getAPIBase } from "@/core/config";
import { LOGIN_URL } from "@/core/urls";
import { ApolloClient, ApolloProvider, InMemoryCache } from "@apollo/client";
import { relayStylePagination } from "@apollo/client/utilities";
import { ApolloLink } from "apollo-link";
import { setContext } from "apollo-link-context";
import { onError } from "apollo-link-error";
import { HttpLink } from "apollo-link-http";
import { RetryLink } from "apollo-link-retry";

import { useAuthContext } from "./AuthProvider";
import Loading from "./Loading";

function getClient(csrfToken, refreshAuth) {
  return new ApolloClient({
    connectToDevTools: false,
    link: ApolloLink.from([
      new RetryLink({
        delay: {
          initial: 500,
          max: Infinity,
          jitter: true
        },
        attempts: {
          max: 5,
          retryIf: (error, _operation) => {
            if (error?.statusCode === 403) {
              return false;
            }
            return !!error;
          }
        }
      }),
      onError(({ graphQLErrors, networkError, operation }) => {
        const skipLoginRedirectOperationNames = new Set([
          "PatientPayCheckoutPage",
          "ProceedToPatientPayCheckoutMutation",
          "ResendPatientPayCheckoutLinkMutation"
        ]);

        if (graphQLErrors) {
          graphQLErrors.forEach(({ message, locations, path }) => {
            console.log(
              `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
            );
            if (
              !skipLoginRedirectOperationNames.has(operation.operationName) &&
              (message.includes("You do not have permission to perform this action") ||
                message.includes("User is not authenticated"))
            ) {
              // If for some reason the session is no longer valid, hard refresh to the login page
              window.location.href = `${LOGIN_URL}?next=${window.location.pathname}`;
            }
          });
        }
        if (networkError) console.log(`[Network error]: ${networkError}`);
        if (networkError && networkError.statusCode === 403) {
          const otpRequired = graphQLErrors?.some(({ message }) =>
            message.includes("OTP verification required")
          );
          if (otpRequired) {
            refreshAuth();
            return;
          }
          window.location.reload();
        }
      }),
      setContext((_, { headers }) => {
        return {
          headers: {
            ...headers,
            "X-CSRFToken": csrfToken
          }
        };
      }),
      new HttpLink({
        uri: `${getAPIBase()}/api/graphql/`,
        credentials: "include"
      })
    ]),
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            examples: {
              keyArgs: ["userId", "userReportId", "verb", "category"]
            },
            expertTestProducts: relayStylePagination(["search", "orderBy"]),
            expertConnections: relayStylePagination([
              "search",
              "orderBy",
              "consultationTypes",
              "supportedTests",
              "practitionerTypes",
              "specialisms",
              "seed",
              "supportedTests",
              "country",
              "language"
            ]),
            practitionerClinicLocations: {
              keyArgs: ["paymentIsoCode", "postCode", "compositeId"]
            },
            userDatapointConnections: relayStylePagination([
              "userReportId",
              "userTestId",
              "excludeGenesRelated",
              "orderBy",
              "search",
              "userId",
              "organisationId"
            ]),
            userResultConnections: relayStylePagination([
              "subsectorRelatedOnly",
              "orderBy",
              "search",
              "userId",
              "userTestId",
              "displayGroup",
              "organisationId"
            ]),
            supplementConnections: relayStylePagination([
              "orderBy",
              "search",
              "productAudience",
              "brandNames",
              "doseTypes",
              "dietryRestrictions"
            ]),
            consumerTestProductConnections: relayStylePagination([
              "orderBy",
              "search",
              "productAudience",
              "gender",
              "testingServices",
              "healthAreas",
              "sampleTypes",
              "isoCode",
              "paymentIsoCode"
            ]),
            courseConnections: relayStylePagination([
              "orderBy",
              "search",
              "testingServices",
              "healthAreas",
              "sampleTypes"
            ]),
            practitionerTestProductConnections: relayStylePagination([
              "orderBy",
              "search",
              "productAudience",
              "gender",
              "testingServices",
              "healthAreas",
              "sampleTypes",
              "isoCode",
              "paymentIsoCode",
              "organisation",
              "filterByFavourites",
              "featured",
              "listingVisibility",
              "userId"
            ]),
            webinarConnections: relayStylePagination([
              "orderBy",
              "search",
              "testingServices",
              "healthAreas",
              "sampleTypes",
              "upcoming",
              "exclude"
            ]),
            documentConnections: relayStylePagination(["orderBy", "search"]),
            eventConnections: relayStylePagination(["orderBy", "search", "types"]),
            faqConnections: relayStylePagination(["orderBy", "search", "topics"]),
            videoGuideConnections: relayStylePagination(["orderBy", "search", "topics", "exclude"])
          }
        },
        ClinicLocationType: {
          keyFields: ["id", "paymentIsoCode", "organisation"]
        },
        TestProductType: {
          keyFields: ["id", "paymentIsoCode", "organisation"]
        },
        TestProductOptionType: {
          keyFields: ["id", "paymentIsoCode", "organisation"]
        },
        UserTestType: {
          fields: {
            userResultConnections: relayStylePagination(["orderBy", "displayGroup"])
          }
        },
        UserReportType: {
          fields: {
            userResultConnections: relayStylePagination(["orderBy", "search"])
          }
        },
        CourseType: {
          fields: {
            testProducts: {
              keyFields: ["paymentIsoCode", "organisation"]
            }
          }
        },
        WebinarType: {
          fields: {
            testProducts: {
              keyFields: ["paymentIsoCode", "organisation"]
            }
          }
        },
        OrganisationTeamMemberType: {
          keyFields: ["id"]
        }
      }
    })
  });
}

export default function ApolloManager({ children }) {
  const { csrfToken, refreshAuth } = useAuthContext();

  const [apolloClient, setApolloClient] = useState(null);

  useEffect(() => {
    if (csrfToken !== undefined) {
      setApolloClient(getClient(csrfToken, refreshAuth));
    }
  }, [csrfToken, refreshAuth]);

  if (apolloClient === null) {
    return <Loading />;
  } else {
    return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
  }
}
